import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  invoiceCompanyOnlyKeys,
  invoiceKeyNames,
  invoicePersonOnlyKeys,
} from '@web/app/constants/invoice.constants';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { toast } from '@web/util/toast';
import { CountriesCompleteService, CountryComplete } from '@services/countries-complete.service';
import { locale } from '@util/locale';
import { ValueInListValidator } from '@services/validators/value-in-list-validator';
import { StorageService } from '@services/storage.service';

@Component({
  selector: 'web-invoice-data',
  templateUrl: './invoice-data.component.html',
  styleUrls: ['./invoice-data.component.scss']
})
export class InvoiceDataDialogComponent {
  invoiceData!: FormGroup;
  invoiceType: 'person' | 'company' = 'person';

  originalCountries: CountryComplete[] = [];
  countriesIsoAndNames: {name: string, iso: string}[] = [];
  filteredCountriesIsoAndNames: {name: string, iso: string}[] = [];
  lang = locale().locale;

  get invoiceKeyNames() {
    if (this.invoiceType === 'company') {
      return invoiceKeyNames.filter((x: string) => !invoicePersonOnlyKeys.includes(x));
    }
    return invoiceKeyNames.filter((x: string) => !invoiceCompanyOnlyKeys.includes(x));
  }

  get requiredFields() {
    if (this.invoiceType === 'person') {
      return [
        'firstName',
        'lastName',
        'fiscalCode',
        'billingCountry',
        'billingAddress',
        'billingCity',
        'billingProvince',
        'billingZipCode',
      ];
    }
    return [
      'companyName',
      'vat',
      'fiscalCode',
      'sdi',
      'billingCountry',
      'billingAddress',
      'billingCity',
      'billingProvince',
      'billingZipCode',
    ];
  }

  isRequired(field: string) {
    return this.requiredFields.includes(field);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private dlg: MatDialogRef<InvoiceDataDialogComponent>,
    private formBuilder: FormBuilder,
    private countriesCompleteService: CountriesCompleteService,
    private storageService: StorageService,
  ) {
    this.invoiceTypeChanged(data.invoiceData);
    
    this.displayCountry = this.displayCountry.bind(this);
    
    this.countriesCompleteService.list().subscribe(country => {
      if (country) {
        this.originalCountries = country;
        this.getCountryIsoCodeData(country);
        this.invoiceData.get('billingCountry')?.setValidators([ValueInListValidator.fromList(this.countriesIsoAndNames.map(x => x.iso))]);
        this.invoiceTypeChanged(data.invoiceData);
      }
    });
  }

  displayCountry(iso: string, o?: {name: string, iso: string}): string {
    var obj = o || this.countriesIsoAndNames?.find(country => country.iso === iso);
    return obj ? obj.name : '';
  }

  private getCountryIsoCodeData(countries: CountryComplete[]) {
    countries.forEach(country => {
      this.countriesIsoAndNames.push({
        name: country.translations[this.lang as keyof {en: string, it: string}] || country.name,
        iso: country.alpha2Code
      });
    })

    this.countriesIsoAndNames = this.sortCountries(this.countriesIsoAndNames);

    this.filteredCountriesIsoAndNames = this.countriesIsoAndNames;
  }

  sortCountries(countries: {name: string, iso: string}[]): {name: string, iso: string}[] {
    let topCountries = this.storageService.load('topCountries') || [];

    let italy = countries.find(country => country.iso === 'IT');

    let topUsedCountries = topCountries
        .filter((topCountry: any) => topCountry.iso !== 'IT')
        .slice(0, 2)
        .map((topCountry: any) => {
            return countries.find(country => country.iso === topCountry.iso);
        })
        .filter(Boolean);

    let remainingCountries = countries.filter(country => 
        country.iso !== 'IT' && 
        !topUsedCountries.some((topCountry: any) => topCountry.iso === country.iso)
    ).sort((a, b) => a.name.localeCompare(b.name));

    return [
        ...(italy ? [italy] : []), 
        ...topUsedCountries, 
        ...remainingCountries
    ];
  }

  filterCountries(event: Event) {
    let searchText = (event.target as HTMLInputElement).value || '';
    searchText = searchText.toLowerCase();
  
    this.filteredCountriesIsoAndNames = this.countriesIsoAndNames.filter(country => 
      country.iso.toLowerCase().includes(searchText?.toLowerCase()) ||
      country.name.toLowerCase().includes(searchText?.toLowerCase())
    );
  }

  closeClicked() {
    this.dlg.close();
  }

  saveClicked() {
    this.invoiceData.markAllAsTouched();
    if (this.invoiceData.invalid) {
      toast('Fill in the required information.');
      return;
    }
    
    let v = { ...this.invoiceData.value };
    v.billingCountry = this.originalCountries
      ?.filter(country => country.alpha2Code === v.billingCountry)
      ?.map(c => ({
        iso: c.alpha2Code,
        name: c.name,
        nameIt: c.translations.it,
        nameEn: c.translations.en,
      }))
      ?.find(Boolean);

    if (this.invoiceType === 'person') {
      delete v.companyName;
      delete v.vat;
    } else {
      delete v.firstName;
      delete v.lastName;
    }

    this.updateTopCountries({
      name: this.displayCountry(v.billingCountry?.iso || v.billingCountry),
      iso: v.billingCountry?.iso || v.billingCountry
    });

    this.dlg.close(v);
  }

  invoiceTypeChanged(incomingData?: any) {
    if (incomingData && !!incomingData.companyName) {
      this.invoiceType = 'company';
    } else if (
      incomingData &&
      !incomingData.companyName &&
      (incomingData.firstName || incomingData.lastName || incomingData.fiscalCode)
    ) {
      this.invoiceType = 'person';
    }

    let data = { ...(incomingData ?? this.invoiceData?.value ?? {}) };

    // User switched Privato ↔ Azienda: clear cross-entity name fields (never keep buyer/prefill).
    if (!incomingData) {
      if (this.invoiceType === 'person') {
        data.companyName = '';
        data.vat = '';
        data.firstName = '';
        data.lastName = '';
      } else {
        data.firstName = '';
        data.lastName = '';
      }
    }

    this.invoiceData = this.formBuilder.group(
      invoiceKeyNames.reduce((acc: { [key: string]: any }, key: string) => {
        acc[key] = [data[key]?.iso ?? data[key] ?? ''];
        return acc;
      }, {} as { [key: string]: any })
    );

    this.addValidators();
  }

  addValidators() {
    Object.values(this.invoiceData.controls).forEach(control => {
      control.clearValidators();
      control.clearAsyncValidators();
    });

    this.requiredFields.forEach(field => {
      this.invoiceData.get(field)?.setValidators([Validators.required]);
    });
    
    this.invoiceData.get('billingCountry')?.setValidators([Validators.required, ValueInListValidator.fromList(this.countriesIsoAndNames.map(x => x.iso))]);
  }

  updateTopCountries(country: {name: string, iso: string}) {
    let topCountries = this.storageService.load('topCountries');
    if (!topCountries) {
      topCountries = this.countriesIsoAndNames.map(x => ({...x, count: 0}));
    }
    let countryIndex = topCountries.findIndex((x: any) => x.iso === country.iso);
    if (countryIndex >= 0) {
      topCountries[countryIndex].count++;
    }
    topCountries = topCountries.sort((a: any, b: any) => b.count - a.count);
    this.storageService.save('topCountries', topCountries);
  }
}
